import { toPng } from "html-to-image";

// END SCREEN downloads (client-side, every seat):
//   porosity-<date>-board.png  the final board + players panel + final scores (a capture of the page)
//   porosity-<date>-log.txt    settings, round-by-round log, weather and sea rise results, final scores

export const dateStamp = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function saveBlobUrl(url, name) {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function downloadText(name, text) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  saveBlobUrl(url, name);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Capture `node` (the main game area). Nodes marked data-capture="skip" are left out.
export async function captureBoardPng(node) {
  if (!node) throw new Error("Nothing to capture");
  const bg = getComputedStyle(node).backgroundColor || "#F4F1EA";
  return toPng(node, {
    pixelRatio: 2,
    cacheBust: true,
    backgroundColor: bg,
    width: node.scrollWidth,
    height: node.scrollHeight,
    filter: (el) => !(el?.dataset && el.dataset.capture === "skip"),
  });
}

export async function downloadBoardImage(node) {
  const url = await captureBoardPng(node);
  const name = `porosity-${dateStamp()}-board.png`;
  saveBlobUrl(url, name);
  return name;
}

// `round` runs one past the last round once the game is over; `rounds_played` is the last round played.
export const roundsPlayed = (g) => g.rounds_played ?? Math.max(0, (g.round || 1) - 1);

const REASON = {
  three_degrees: (g) => `the world reached 3° in round ${roundsPlayed(g)}; most resilience wins (debt is not subtracted)`,
  max_rounds: (g) => `${roundsPlayed(g)} rounds played; population + resilience − debt wins`,
};

const SLR_KINDS = new Set(["slr", "slr_loss", "slr_held", "slr_mangrove", "catch_up"]);

// Plain-text game log.
export function buildGameLogText(game, scoring = null) {
  const lines = [];
  const players = game.players || [];
  const nameOf = (c) => players.find((p) => p.color === c)?.name || c;
  const log = game.game_log || [];
  const rule = (t) => {
    lines.push("");
    lines.push(t);
    lines.push("=".repeat(t.length));
  };

  lines.push("POROSITY — game log");
  lines.push(`Saved ${new Date().toLocaleString()}`);
  rule("Game settings");
  lines.push(`Mode: ${game.online ? `online${game.lobby_code ? ` (lobby ${game.lobby_code})` : ""}` : "same device (pass and play)"}`);
  lines.push(`Players (seat order): ${players.map((p, i) => `${i + 1}. ${p.name} (${p.color})`).join(", ")}`);
  lines.push(`Board: ${game.grid_cols} columns × ${game.grid_rows} rows (${game.grid_cols * game.grid_rows} squares), sea along the bottom edge`);
  const heights = Object.values(game.terrain || {});
  if (heights.length) lines.push(`Terrain heights: ${[1, 2, 3, 4].map((h) => `▲${h} × ${heights.filter((v) => Number(v) === h).length}`).join(", ")}`);
  if (game.carbon) lines.push(`Carbon meter: ${game.carbon.per_degree} carbon per degree; the sea rises at 1° / 2° / 3° · final meter ${game.carbon.meter} (${game.carbon.temp}°), city sink ${game.carbon.city_sink}, shared reserve ${game.carbon.shared_reserve}`);
  lines.push(`Rounds played: ${roundsPlayed(game)} · Sea level rises: ${game.slr_events ?? 0}`);
  if (game.game_over) lines.push(`Game over: ${(REASON[game.endgame_reason] || (() => "the game ended"))(game)}.`);

  rule("Round by round");
  if (log.length === 0) lines.push("(no entries)");
  let current = null;
  for (const e of log) {
    const r = e.round ?? 0;
    if (r !== current) {
      current = r;
      lines.push("");
      lines.push(r === 0 ? "Phase 1 (setup)" : `Round ${r}`);
    }
    lines.push(`  - ${e.text}`);
  }

  rule("Weather results");
  const weather = game.weather_log || [];
  if (weather.length === 0) lines.push("(no weather)");
  for (const w of weather) {
    lines.push(`Round ${w.round}: ${w.event}`);
    for (const e of log) {
      if ((e.round ?? 0) !== w.round) continue;
      if (e.kind === "water" || (e.kind === "weather" && !/the weather card drawn is/.test(e.text || ""))) lines.push(`  - ${e.text}`);
    }
  }

  rule("Sea level rise results");
  const slr = log.filter((e) => SLR_KINDS.has(e.kind));
  if (slr.length === 0) lines.push("(the sea never rose)");
  for (const e of slr) lines.push(`Round ${e.round}: ${e.text}`);

  rule("Final scores");
  const fb = scoring || game.final_breakdown;
  const rows = fb?.ranking || fb?.breakdowns || [];
  if (!rows.length) lines.push("(not scored yet)");
  rows.forEach((b, i) => {
    const place = b.place ?? i + 1;
    lines.push(`${place}. ${nameOf(b.color)} (${b.color}) — ${b.total} points`);
    const rp = b.resilience_parts || {};
    const early = b.mode === "early";
    lines.push(`   Population ${b.population}${early ? " (not counted at 3°)" : ""}`);
    lines.push(`   Resilience ${b.resilience}: green and blue tiles ${rp.tiles ?? 0}, Green Roofs and Streets ${rp.upgrades ?? 0}, network bonus ${rp.network ?? 0}, grey buildings ${rp.grey ?? 0} (${rp.grey_count ?? 0})`);
    lines.push(`   Debt ${b.debt}${early ? " (not subtracted at 3°)" : ` = ${b.debt_penalty}`}`);
    lines.push(`   Repairs paid over the game: ${b.flood_paid ?? 0} (tiebreak: least repairs wins)`);
  });
  if (fb?.shared_victory) lines.push(`Shared victory: ${(fb.winner_colors || []).map(nameOf).join(" & ")}`);
  else if (fb?.winner_colors?.length) lines.push(`Winner: ${nameOf(fb.winner_colors[0])}`);
  lines.push("");
  return lines.join("\n");
}

export function downloadGameLog(game, scoring = null) {
  const name = `porosity-${dateStamp()}-log.txt`;
  downloadText(name, buildGameLogText(game, scoring));
  return name;
}
